import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import axios from 'api/axiosInstance';
import endpoints from 'api/endpoints';

// ==============================|| SUPER ADMIN BANNER ||============================== //

export default function SuperAdminBanner() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const isViewingAsAdmin = localStorage.getItem('viewingAsAdmin') === 'true';
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');

  const handleExit = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Call revert token API
      const response = await axios.post(
        endpoints.company.revertToken,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      console.log('Revert token response:', response.data);

      // Update localStorage with response data
      const data = response.data;
      if (data.access) {
        localStorage.setItem('token', data.access);
      }
      if (data.refresh) {
        localStorage.setItem('refreshToken', data.refresh);
      }
      if (data.role) {
        localStorage.setItem('role', data.role);
      }
      // Super admins have no tenant — explicitly drop the impersonated
      // company's tenant_id (otherwise dashboard tries to fetch the old
      // tenant's data with the new super-admin token, gets 401/403, and
      // the axios interceptor sends us back to /login).
      if (data.company_id) {
        localStorage.setItem('tenant_id', data.company_id);
      } else {
        localStorage.removeItem('tenant_id');
      }
      const user = {
        id: data.user_id || data.id,
        username: data.username,
        email: data.email || data.username,
        role: data.role,
        company: data.company,
        company_id: data.company_id || null,
        company_name: data.company_name || null,
        industry: data.industry,
        industry_code: data.industry_code,
        industry_name: data.industry_name,
      };
      localStorage.setItem('userData', JSON.stringify(user));

      // Clear admin viewing flags
      localStorage.removeItem('viewingAsAdmin');
      localStorage.removeItem('superAdminData');

      // Super admins & marketing land on the company/leads list, not the
      // tenant dashboard.
      const target = data.role === 'SUPER_ADMIN' || data.role === 'MARKETING' ? '/companyList' : '/dashboard';
      navigate(target);
      window.location.reload();
    } catch (error) {
      console.error('Revert token failed:', error);

      // Fallback: restore from stored super admin data
      const superAdminData = JSON.parse(localStorage.getItem('superAdminData') || '{}');
      let restoredRole = '';
      if (superAdminData.token) {
        localStorage.setItem('token', superAdminData.token);
        localStorage.setItem('refreshToken', superAdminData.refreshToken || '');
        localStorage.setItem('role', superAdminData.role || '');
        restoredRole = superAdminData.role || '';
        if (superAdminData.tenant_id) {
          localStorage.setItem('tenant_id', superAdminData.tenant_id);
        } else {
          localStorage.removeItem('tenant_id');
        }
        localStorage.setItem('userData', superAdminData.userData || '{}');
      }

      // Clear admin viewing flags
      localStorage.removeItem('viewingAsAdmin');
      localStorage.removeItem('superAdminData');

      const target = restoredRole === 'SUPER_ADMIN' || restoredRole === 'MARKETING' ? '/companyList' : '/dashboard';
      navigate(target);
      window.location.reload();
    } finally {
      setLoading(false);
    }
  };

  if (!isViewingAsAdmin) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
        bgcolor: '#1976d2',
        color: 'white',
        py: 0.75,
        px: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        You are viewing {userData.company ? userData.company : 'this company'} — exit to return to your account
      </Typography>
      <Button
        variant="contained"
        size="small"
        onClick={handleExit}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <LogoutOutlined />}
        sx={{
          bgcolor: 'white',
          color: '#1976d2',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.9)'
          },
          textTransform: 'none',
          py: 0.25,
          px: 1.5,
          minHeight: 'auto'
        }}
      >
        {loading ? 'Exiting...' : 'Exit'}
      </Button>
    </Box>
  );
}
